import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './globalforecasting.css';

const GlobalForecasting = () => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState('');
  const [currentScenarioName, setCurrentScenarioName] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Amazon Sales and Marketing data
  const [amazonData, setAmazonData] = useState([]);
  const [originalMomentumSales, setOriginalMomentumSales] = useState({});
  const [lastThreeMonthsAverages, setLastThreeMonthsAverages] = useState(null);
  
  // Shopify Sales and Marketing data
  const [shopifyData, setShopifyData] = useState([]);
  const [originalShopifyMomentumSales, setOriginalShopifyMomentumSales] = useState({});
  const [shopifyLastThreeMonthsAverages, setShopifyLastThreeMonthsAverages] = useState(null);
  
  // Trade Sales data
  const [tradeData, setTradeData] = useState([]);
  const [originalTradeMomentumSales, setOriginalTradeMomentumSales] = useState({});
  
  // Headcount data
  const [headcountData, setHeadcountData] = useState([]);
  
  // Progress dialog state
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressStep, setProgressStep] = useState(0);
  
  const tabs = [
    { id: 0, name: 'Amazon Sales and Marketing' },
    { id: 1, name: 'Shopify Marketing and Sales Assumptions' },
    { id: 2, name: 'Trade Sales Assumptions' },
    { id: 3, name: 'Headcount Assumptions' },
    { id: 4, name: 'Other Assumptions' }
  ];

  // Fetch available scenarios
  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/global-forecast/scenarios`);
        setScenarios(response.data.scenarios || []);
      } catch (error) {
        console.error('Error fetching scenarios:', error);
      }
    };
    fetchScenarios();
  }, []);

  // Fetch Amazon data when component mounts
  useEffect(() => {
    fetchAmazonData();
    fetchShopifyData();
    fetchTradeData();
    fetchHeadcountData();
  }, []);

  const fetchAmazonData = async (scenarioName = null) => {
    setLoading(true);
    setError('');
    try {
      const url = scenarioName 
        ? `${API_BASE_URL}/api/global-forecast/amazon-data?scenario_name=${encodeURIComponent(scenarioName)}`
        : `${API_BASE_URL}/api/global-forecast/amazon-data`;
      
      const response = await axios.get(url);
      setAmazonData(response.data.months || []);
      setLastThreeMonthsAverages(response.data.last_three_months_averages || null);
      
      // Store original momentum sales for save calculations (only on initial load, not when loading scenarios)
      if (!scenarioName) {
        const originalSales = {};
        if (response.data.months) {
          response.data.months.forEach(month => {
            originalSales[month.month] = month.momentum_sales;
          });
        }
        setOriginalMomentumSales(originalSales);
      }
    } catch (error) {
      console.error('Error fetching Amazon data:', error);
      setError(`Failed to load Amazon data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchShopifyData = async (scenarioName = null) => {
    setLoading(true);
    setError('');
    try {
      const url = scenarioName 
        ? `${API_BASE_URL}/api/global-forecast/shopify-data?scenario_name=${encodeURIComponent(scenarioName)}`
        : `${API_BASE_URL}/api/global-forecast/shopify-data`;
      
      const response = await axios.get(url);
      setShopifyData(response.data.months || []);
      setShopifyLastThreeMonthsAverages(response.data.last_three_months_averages || null);
      
      // Store original momentum sales for save calculations (only on initial load, not when loading scenarios)
      if (!scenarioName) {
        const originalSales = {};
        if (response.data.months) {
          response.data.months.forEach(month => {
            originalSales[month.month] = month.momentum_sales;
          });
        }
        setOriginalShopifyMomentumSales(originalSales);
      }
    } catch (error) {
      console.error('Error fetching Shopify data:', error);
      setError(`Failed to load Shopify data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchTradeData = async (scenarioName = null) => {
    setLoading(true);
    setError('');
    try {
      const url = scenarioName 
        ? `${API_BASE_URL}/api/global-forecast/trade-data?scenario_name=${encodeURIComponent(scenarioName)}`
        : `${API_BASE_URL}/api/global-forecast/trade-data`;
      
      const response = await axios.get(url);
      const monthsData = response.data.months || [];
      
      // Initialize with default sales multiplier of 1.00 if not present
      const initializedData = monthsData.map(month => ({
        ...month,
        sales_multiplier: month.sales_multiplier || 1.00
      }));
      
      setTradeData(initializedData);
      
      // Store original momentum sales for save calculations (only on initial load, not when loading scenarios)
      if (!scenarioName) {
        const originalSales = {};
        if (initializedData) {
          initializedData.forEach(month => {
            originalSales[month.month] = month.momentum_sales;
          });
        }
        setOriginalTradeMomentumSales(originalSales);
      }
    } catch (error) {
      console.error('Error fetching Trade data:', error);
      setError(`Failed to load Trade data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeadcountData = async (scenarioName = null) => {
    setLoading(true);
    setError('');
    try {
      const url = scenarioName 
        ? `${API_BASE_URL}/api/global-forecast/headcount-data?scenario_name=${encodeURIComponent(scenarioName)}`
        : `${API_BASE_URL}/api/global-forecast/headcount-data`;
      
      const response = await axios.get(url);
      const headcountRecords = response.data.headcount || [];
      
      // If no data exists, initialize with CEO row
      if (headcountRecords.length === 0) {
        const currentMonth = new Date().toISOString().slice(0, 7);
        setHeadcountData([{ 
          role: 'CEO', 
          salary: '11000', 
          hire_month: currentMonth,
          is_ceo: true 
        }]);
      } else {
        // Check if CEO exists in loaded data, if not add it
        const hasCEO = headcountRecords.some(record => record.role === 'CEO');
        if (!hasCEO) {
          const currentMonth = new Date().toISOString().slice(0, 7);
          headcountRecords.unshift({ 
            role: 'CEO', 
            salary: '11000', 
            hire_month: currentMonth,
            is_ceo: true 
          });
        }
        setHeadcountData(headcountRecords);
      }
    } catch (error) {
      console.error('Error fetching Headcount data:', error);
      setError(`Failed to load Headcount data: ${error.message}`);
      // Initialize with CEO row on error
      const currentMonth = new Date().toISOString().slice(0, 7);
      setHeadcountData([{ 
        role: 'CEO', 
        salary: '11000', 
        hire_month: currentMonth,
        is_ceo: true 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const loadScenario = async (scenarioName) => {
    if (!scenarioName) return;
    
    setLoading(true);
    setError('');
    try {
      setCurrentScenarioName(scenarioName);
      
      // Fetch Amazon data with scenario name
      await fetchAmazonData(scenarioName);
      
      // Fetch Shopify data with scenario name
      await fetchShopifyData(scenarioName);
      
      // Fetch Trade data with scenario name
      await fetchTradeData(scenarioName);
      
      // Fetch Headcount data with scenario name
      await fetchHeadcountData(scenarioName);
      
    } catch (error) {
      console.error('Error loading scenario:', error);
      setError(`Failed to load scenario: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveScenario = async (scenarioName) => {
    if (!scenarioName) {
      setError('Please enter a scenario name');
      return;
    }
    
    setLoading(true);
    setError('');
    setShowProgressDialog(true);
    setProgressStep(0);
    setProgressMessage('Saving scenario...');
    
    try {
      // Ensure we have all 12 months of data before saving
      // Generate the next 12 months starting from current month
      const generateAllMonths = () => {
        const months = [];
        const currentDate = new Date();
        for (let i = 0; i < 12; i++) {
          const month = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
          months.push(month.toISOString().slice(0, 7)); // YYYY-MM format
        }
        return months;
      };

      const allMonths = generateAllMonths();
      
      // Ensure Amazon data has all 12 months
      const completeAmazonData = allMonths.map(month => {
        const existingData = amazonData.find(data => data.month === month);
        if (existingData) {
          return existingData;
        } else {
          // Use momentum forecast values for missing months
          const originalSales = originalMomentumSales[month] || 0;
          return {
            month: month,
            marketing_spend: 0, // Will be calculated by backend
            momentum_sales: originalSales,
            roas: 0 // Will be calculated by backend
          };
        }
      });

      // Ensure Shopify data has all 12 months
      const completeShopifyData = allMonths.map(month => {
        const existingData = shopifyData.find(data => data.month === month);
        if (existingData) {
          return existingData;
        } else {
          // Use momentum forecast values for missing months
          const originalSales = originalShopifyMomentumSales[month] || 0;
          return {
            month: month,
            marketing_spend: 0, // Will be calculated by backend
            momentum_sales: originalSales,
            roas: 0 // Will be calculated by backend
          };
        }
      });

      // Ensure Trade data has all 12 months
      const completeTradeData = allMonths.map(month => {
        const existingData = tradeData.find(data => data.month === month);
        if (existingData) {
          return existingData;
        } else {
          // Use momentum forecast values for missing months
          const originalSales = originalTradeMomentumSales[month] || 0;
          return {
            month: month,
            momentum_sales: originalSales,
            sales_multiplier: 1.00 // Default multiplier
          };
        }
      });

      const scenarioData = {
        scenario_name: scenarioName,
        amazon_data: completeAmazonData,
        shopify_data: completeShopifyData,
        trade_data: completeTradeData,
        headcount_data: headcountData,
        original_momentum_sales: { ...originalMomentumSales, ...originalShopifyMomentumSales, ...originalTradeMomentumSales },
        run_forecast_updates: true
      };
      
      // Show that we're processing the request
      setProgressStep(1);
      setProgressMessage('Saving scenario and updating forecasts...');
      
      const saveResponse = await axios.post(`${API_BASE_URL}/api/global-forecast/save-scenario`, scenarioData);
      
      if (saveResponse.data.success) {
        // Show intermediate step briefly
        setProgressStep(2);
        setProgressMessage('Sales forecasts updated, updating PL forecasts...');
        
        // Small delay to make the intermediate step visible
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // All steps completed successfully
        setProgressStep(3);
        setProgressMessage('All forecasts updated successfully!');
        
        setCurrentScenarioName(scenarioName);
        
        // Refresh scenarios list
        const response = await axios.get(`${API_BASE_URL}/api/global-forecast/scenarios`);
        setScenarios(response.data.scenarios || []);
        
        // Show success message briefly, then close dialog
        setTimeout(() => {
          setShowProgressDialog(false);
        }, 1500);
      } else {
        throw new Error(saveResponse.data.message || 'Failed to save scenario');
      }
    } catch (error) {
      console.error('Error saving scenario:', error);
      setError(`Failed to save scenario: ${error.message}`);
      setShowProgressDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const updateAmazonData = (monthIndex, field, value) => {
    const updatedData = [...amazonData];
    updatedData[monthIndex] = {
      ...updatedData[monthIndex],
      [field]: value
    };
    
    // Recalculate Momentum Sales if marketing spend or ROAS changed
    if (field === 'marketing_spend' || field === 'roas') {
      const marketingSpend = parseFloat(updatedData[monthIndex].marketing_spend) || 0;
      const roas = parseFloat(updatedData[monthIndex].roas) || 0;
      updatedData[monthIndex].momentum_sales = (marketingSpend * roas).toFixed(2);
    }
    
    setAmazonData(updatedData);
  };

  const updateShopifyData = (monthIndex, field, value) => {
    const updatedData = [...shopifyData];
    updatedData[monthIndex] = {
      ...updatedData[monthIndex],
      [field]: value
    };
    
    // Recalculate Momentum Sales if marketing spend or ROAS changed
    if (field === 'marketing_spend' || field === 'roas') {
      const marketingSpend = parseFloat(updatedData[monthIndex].marketing_spend) || 0;
      const roas = parseFloat(updatedData[monthIndex].roas) || 0;
      updatedData[monthIndex].momentum_sales = (marketingSpend * roas).toFixed(2);
    }
    
    setShopifyData(updatedData);
  };

  const updateTradeData = (monthIndex, field, value) => {
    const updatedData = [...tradeData];
    updatedData[monthIndex] = {
      ...updatedData[monthIndex],
      [field]: value
    };
    
    // Recalculate Projected Sales if sales multiplier changed
    if (field === 'sales_multiplier') {
      const momentumSales = parseFloat(updatedData[monthIndex].momentum_sales) || 0;
      const salesMultiplier = parseFloat(value) || 0;
      updatedData[monthIndex].projected_sales = (momentumSales * salesMultiplier).toFixed(2);
    }
    
    setTradeData(updatedData);
  };

  const updateHeadcountData = (index, field, value) => {
    const updatedData = [...headcountData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: value
    };
    setHeadcountData(updatedData);
  };

  const addHeadcountRow = () => {
    setHeadcountData([...headcountData, { role: '', salary: '', hire_month: '', is_ceo: false }]);
  };

  const removeHeadcountRow = (index) => {
    // Don't allow removing the CEO row (first row)
    if (headcountData.length > 1 && index !== 0) {
      const updatedData = headcountData.filter((_, i) => i !== index);
      setHeadcountData(updatedData);
    }
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    // Add current month
    const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    options.push({
      value: currentMonth.toISOString().slice(0, 7),
      label: currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    });
    
    // Add next 12 months
    for (let i = 1; i <= 12; i++) {
      const futureMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      options.push({
        value: futureMonth.toISOString().slice(0, 7),
        label: futureMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    
    return options;
  };

  const nextTab = () => {
    setActiveTab((prev) => (prev + 1) % tabs.length);
  };

  const prevTab = () => {
    setActiveTab((prev) => (prev - 1 + tabs.length) % tabs.length);
  };

  return (
    <div className="global-forecasting-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Global Forecasting Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Manage global forecasting assumptions across all channels</li>
              <li>Create and save different forecasting scenarios</li>
              <li>Set marketing spend, ROAS, and headcount assumptions</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {/* Scenario Management */}
        <div className="scenario-management">
        <div className="scenario-controls">
          <div className="scenario-selector">
            <label htmlFor="scenario-select">Load Existing Scenario:</label>
            <select
              id="scenario-select"
              value={selectedScenario}
              onChange={(e) => {
                setSelectedScenario(e.target.value);
                if (e.target.value) {
                  loadScenario(e.target.value);
                }
              }}
              disabled={loading}
            >
              <option value="">Choose a scenario...</option>
              {scenarios.map((scenario) => (
                <option key={scenario} value={scenario}>
                  {scenario}
                </option>
              ))}
            </select>
          </div>
          
          <div className="scenario-save">
            <input
              type="text"
              placeholder="Enter scenario name"
              value={currentScenarioName}
              onChange={(e) => setCurrentScenarioName(e.target.value)}
              disabled={loading}
            />
            <button 
              onClick={() => saveScenario(currentScenarioName)}
              disabled={loading || !currentScenarioName}
              className="save-button"
            >
              {loading ? 'Saving...' : 'Save Scenario'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          onClick={prevTab} 
          className="tab-arrow"
          disabled={loading}
        >
          ‹
        </button>
        
        <div className="tab-indicator">
          {tabs[activeTab].name}
        </div>
        
        <button 
          onClick={nextTab} 
          className="tab-arrow"
          disabled={loading}
        >
          ›
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 0 && (
          <div className="amazon-sales-marketing">
            <h2>Amazon Sales and Marketing Assumptions</h2>
            
            {loading ? (
              <div className="loading">Loading data...</div>
            ) : (
              <div className="assumptions-table-container">
                <table className="assumptions-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Marketing Spend ($)</th>
                      <th>Momentum Sales ($)</th>
                      <th>ROAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Last 3 Months Averages Row */}
                    {lastThreeMonthsAverages && (
                      <tr className="averages-row">
                        <td className="averages-label">Last 3 Month Averages</td>
                        <td className="averages-value">
                          ${lastThreeMonthsAverages.marketing_spend.toLocaleString()}
                        </td>
                        <td className="averages-value">
                          ${lastThreeMonthsAverages.sales.toLocaleString()}
                        </td>
                        <td className="averages-value">
                          {lastThreeMonthsAverages.roas.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    
                    {/* Separator row */}
                    {lastThreeMonthsAverages && (
                      <tr className="separator-row">
                        <td colSpan="4"></td>
                      </tr>
                    )}
                    
                    {amazonData.map((monthData, index) => {
                      // Convert YYYY-MM format to display format (e.g., "2025-07" -> "July 2025")
                      const displayMonth = (() => {
                        const [year, month] = monthData.month.split('-');
                        const date = new Date(parseInt(year), parseInt(month) - 1);
                        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      })();
                      
                      return (
                        <tr key={index}>
                          <td>{displayMonth}</td>
                        <td>
                          <input
                            type="number"
                            value={monthData.marketing_spend}
                            onChange={(e) => updateAmazonData(index, 'marketing_spend', e.target.value)}
                            className={monthData.is_suggested ? 'suggested-value' : 'user-value'}
                            step="0.01"
                          />
                        </td>
                        <td className="momentum-sales-cell">
                          ${parseFloat(monthData.momentum_sales).toLocaleString()}
                        </td>
                        <td>
                          <input
                            type="number"
                            value={monthData.roas}
                            onChange={(e) => updateAmazonData(index, 'roas', e.target.value)}
                            className={monthData.is_suggested ? 'suggested-value' : 'user-value'}
                            step="0.01"
                          />
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 1 && (
          <div className="shopify-assumptions">
            <h2>Shopify Marketing and Sales Assumptions</h2>
            
            {loading ? (
              <div className="loading">Loading data...</div>
            ) : (
              <div className="assumptions-table-container">
                <table className="assumptions-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Marketing Spend ($)</th>
                      <th>Momentum Sales ($)</th>
                      <th>ROAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Last 3 Months Averages Row */}
                    {shopifyLastThreeMonthsAverages && (
                      <tr className="averages-row">
                        <td className="averages-label">Last 3 Month Averages</td>
                        <td className="averages-value">
                          ${shopifyLastThreeMonthsAverages.marketing_spend.toLocaleString()}
                        </td>
                        <td className="averages-value">
                          ${shopifyLastThreeMonthsAverages.sales.toLocaleString()}
                        </td>
                        <td className="averages-value">
                          {shopifyLastThreeMonthsAverages.roas.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    
                    {/* Separator row */}
                    {shopifyLastThreeMonthsAverages && (
                      <tr className="separator-row">
                        <td colSpan="4"></td>
                      </tr>
                    )}
                    
                    {shopifyData.map((monthData, index) => {
                      // Convert YYYY-MM format to display format (e.g., "2025-07" -> "July 2025")
                      const displayMonth = (() => {
                        const [year, month] = monthData.month.split('-');
                        const date = new Date(parseInt(year), parseInt(month) - 1);
                        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      })();
                      
                      return (
                        <tr key={index}>
                          <td>{displayMonth}</td>
                        <td>
                          <input
                            type="number"
                            value={monthData.marketing_spend}
                            onChange={(e) => updateShopifyData(index, 'marketing_spend', e.target.value)}
                            className={monthData.is_suggested ? 'suggested-value' : 'user-value'}
                            step="0.01"
                          />
                        </td>
                        <td className="momentum-sales-cell">
                          ${parseFloat(monthData.momentum_sales).toLocaleString()}
                        </td>
                        <td>
                          <input
                            type="number"
                            value={monthData.roas}
                            onChange={(e) => updateShopifyData(index, 'roas', e.target.value)}
                            className={monthData.is_suggested ? 'suggested-value' : 'user-value'}
                            step="0.01"
                          />
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 2 && (
          <div className="trade-assumptions">
            <h2>Trade Sales Assumptions</h2>
            
            {loading ? (
              <div className="loading">Loading data...</div>
            ) : (
              <div className="assumptions-table-container">
                <table className="assumptions-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Momentum Sales ($)</th>
                      <th>Sales Multiplier</th>
                      <th>Projected Sales ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradeData.map((monthData, index) => {
                      // Convert YYYY-MM format to display format (e.g., "2025-07" -> "July 2025")
                      const displayMonth = (() => {
                        const [year, month] = monthData.month.split('-');
                        const date = new Date(parseInt(year), parseInt(month) - 1);
                        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      })();
                      
                      // Calculate projected sales
                      const momentumSales = parseFloat(monthData.momentum_sales) || 0;
                      const salesMultiplier = parseFloat(monthData.sales_multiplier) || 1.00;
                      const projectedSales = (momentumSales * salesMultiplier).toFixed(2);
                      
                      return (
                        <tr key={index}>
                          <td>{displayMonth}</td>
                          <td className="momentum-sales-cell">
                            ${momentumSales.toLocaleString()}
                          </td>
                          <td>
                            <input
                              type="number"
                              value={monthData.sales_multiplier}
                              onChange={(e) => updateTradeData(index, 'sales_multiplier', e.target.value)}
                              className="user-value"
                              step="0.01"
                              min="0"
                            />
                          </td>
                          <td className="projected-sales-cell">
                            ${parseFloat(projectedSales).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 3 && (
          <div className="headcount-assumptions">
            <h2>Headcount Assumptions</h2>
            
            {loading ? (
              <div className="loading">Loading data...</div>
            ) : (
              <div className="headcount-container">
                <div className="headcount-controls">
                  <button 
                    onClick={addHeadcountRow}
                    className="add-row-button"
                    type="button"
                  >
                    + Add Row
                  </button>
                </div>
                
                <div className="headcount-table-container">
                  <table className="headcount-table">
                    <thead>
                      <tr>
                        <th>Role</th>
                        <th>Monthly Salary ($)</th>
                        <th>Hire Month</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headcountData.map((row, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              value={row.role}
                              onChange={(e) => updateHeadcountData(index, 'role', e.target.value)}
                              placeholder="Enter role title"
                              className="role-input"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={row.salary}
                              onChange={(e) => updateHeadcountData(index, 'salary', e.target.value)}
                              placeholder="0"
                              min="0"
                              step="1"
                              className="salary-input"
                            />
                          </td>
                          <td>
                            {row.is_ceo ? (
                              <input
                                type="text"
                                value={row.hire_month ? new Date(row.hire_month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : ''}
                                readOnly
                                className="hire-month-readonly"
                              />
                            ) : (
                              <select
                                value={row.hire_month}
                                onChange={(e) => updateHeadcountData(index, 'hire_month', e.target.value)}
                                className="hire-month-select"
                              >
                                <option value="">Select month...</option>
                                {generateMonthOptions().map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            <button
                              onClick={() => removeHeadcountRow(index)}
                              className="remove-row-button"
                              type="button"
                              disabled={headcountData.length === 1 || row.is_ceo}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 4 && (
          <div className="other-assumptions">
            <h2>Other Assumptions</h2>
            <p style={{ fontSize: '1.2em', color: '#888', textAlign: 'center', marginTop: '50px' }}>
              Coming Soon
            </p>
          </div>
        )}
      </div>
      
      {/* Progress Dialog */}
      {showProgressDialog && (
        <div className="progress-dialog-overlay">
          <div className="progress-dialog">
            <h3>Updating Forecasts</h3>
            <div className="progress-steps">
              <div className={`progress-step ${progressStep >= 0 ? 'completed' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-label">Saving Scenario</div>
              </div>
              <div className={`progress-step ${progressStep >= 1 ? 'completed' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-label">Updating Sales Forecasts</div>
              </div>
              <div className={`progress-step ${progressStep >= 2 ? 'completed' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-label">Updating PL Forecasts</div>
              </div>
              <div className={`progress-step ${progressStep >= 3 ? 'completed' : ''}`}>
                <div className="step-number">4</div>
                <div className="step-label">Complete</div>
              </div>
            </div>
            <div className="progress-message">{progressMessage}</div>
            <div className="progress-spinner"></div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default GlobalForecasting; 